import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react';
import { Search } from '@gjensidige/builders-icons';
import { useTracking, useReqireLabel, getLabel } from '../utils';
import { InputGroupContext } from '../input-group/utils';
import type { TrackingProp } from '../types';
import styles from './input.module.css';
import clsx from 'clsx';

export type InputProps = Omit<
  React.ComponentPropsWithoutRef<'input'>,
  'prefix' | 'suffix' | 'type'
> & {
  'data-theme'?: string;
  classNameWrapper?: string;
  internal?: 'small' | 'table-inline';
  invalid?: boolean;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  tracking?: TrackingProp;
  type?:
    | 'checkbox'
    | 'email'
    | 'number'
    | 'password'
    | 'radio'
    | 'search'
    | 'switch'
    | 'tel'
    | 'text'
    | 'url';
  wrapperProps?: React.ComponentPropsWithoutRef<'span'>;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    classNameWrapper: deprecated,
    hidden,
    invalid,
    prefix,
    size,
    style,
    suffix,
    wrapperProps,
    ...rest
  },
  ref
) {
  if (rest.tracking === undefined) rest.tracking = true; // Enable tracking by default, but keep in "rest" for InputGroup augumenting
  const useGroupProps =
    useContext(InputGroupContext)?.useGroupProps || (() => {});
  useGroupProps(rest); // Augment rest with potential props from InputGroup

  const innerRef = useRef<HTMLInputElement>(null);
  const isBool = rest.type === 'checkbox' || rest.type === 'radio';
  const isSwitch = rest.type === 'switch';
  const classes = clsx(
    wrapperProps?.className,
    styles.wrapper,
    deprecated,
    !!size && styles.htmlSize,
    !!rest.internal && styles[`${rest.internal}`],
    !!rest.disabled && styles.isDisabled,
    !!isSwitch && styles.isSwitch,
    !!invalid && styles.isInvalid,
    !!(isBool || isSwitch) && styles.isBoolean,
    !!(!rest.role && rest.readOnly) && styles.isReadOnly // add styling if readonly and no role (f.ex. combobox when selecting options)
  );
  const track = useTracking(rest.tracking);

  useImperativeHandle(ref, () => innerRef.current as HTMLInputElement); // Forward innerRef
  useReqireLabel<HTMLInputElement>(innerRef);

  // Use native change event for tracking as this triggers on actually changed value
  useEffect(() => {
    const input = innerRef.current;
    const onChange = () => {
      const check = input?.checked ? 'checked' : 'unchecked';
      track({
        eventCategory: 'input',
        eventAction: isBool ? check : 'change',
        eventLabel: getLabel(input)
      });
    };

    input?.addEventListener('change', onChange);
    return () => input?.removeEventListener('change', onChange);
  }, [track, isBool]);

  return (
    <span {...wrapperProps} className={classes} hidden={hidden}>
      {isBool || isSwitch || prefix || (rest.type === 'search' && <Search />)}
      <input
        {...{ ...rest, tracking: undefined, internal: undefined }} // Remove tracking and internal from rest to prevent output as HTML attribute
        aria-invalid={rest['aria-invalid'] || invalid || undefined}
        data-internal-small={rest.internal === 'small' || undefined}
        ref={innerRef}
        style={{ ...style, '--size': size } as React.CSSProperties}
        type={rest.type === 'switch' ? 'checkbox' : rest.type}
      />
      {isBool || isSwitch || suffix}
    </span>
  );
});
